import { Box } from '@ace-clublabs/shared-component-library';
import {
  APIProvider,
  Map as GoogleMap,
  Marker,
} from '@vis.gl/react-google-maps';
import { useEffect, useState } from 'react';

import { constants } from '@/constants';
import type { Branch } from '@/features/appointments/utils/Branch';

interface BranchesMapProps {
  branches: Branch[];
  selectedBranch?: Branch | null;
  onSelectBranch: (branch: Branch) => void;
}

const BranchesMap: React.FC<BranchesMapProps> = ({
  branches,
  selectedBranch,
  onSelectBranch,
}) => {
  const defaultCenter = selectedBranch || branches?.[0];
  const [center, setCenter] = useState({
    lat: defaultCenter?.latitude,
    lng: defaultCenter?.longitude,
  });

  useEffect(() => {
    if (selectedBranch?.latitude && selectedBranch?.longitude) {
      setCenter({
        lat: selectedBranch.latitude,
        lng: selectedBranch.longitude,
      });
    }
  }, [selectedBranch]);

  if (!branches || branches.length === 0) {
    return null;
  }

  const mapCenter =
    center.lat !== undefined && center.lng !== undefined
      ? center
      : { lat: defaultCenter.latitude, lng: defaultCenter.longitude };

  return (
    <Box
      sx={{
        width: '100%',
        height: 222,
        borderRadius: '20px',
        overflow: 'hidden',
        backgroundColor: '#F2F4F7',
        position: 'relative',
        mb: 2,
        flexShrink: 0,
      }}
      data-testid='branch-location-map'
    >
      <APIProvider apiKey={constants.GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          center={mapCenter}
          onCenterChanged={(ev) => setCenter(ev.detail.center)}
          defaultZoom={11}
          disableDefaultUI
          streetViewControl
          zoomControl
          gestureHandling='cooperative'
          style={{ width: '100%', height: '100%' }}
        >
          {branches.map((branch) => {
            const isSelected = selectedBranch?.branchId === branch.branchId;

            return (
              <Marker
                key={branch.branchId}
                position={{ lat: branch.latitude, lng: branch.longitude }}
                title={branch.branchName}
                onClick={() => onSelectBranch(branch)}
                icon={{
                  path: 'M23.7018 39.4167C27.3855 34.8191 35.7867 23.6759 35.7867 17.4167C35.7867 9.8256 29.6114 3.66675 22 3.66675C14.3887 3.66675 8.21338 9.8256 8.21338 17.4167C8.21338 23.6759 16.6146 34.8191 20.2983 39.4167C21.1815 40.5125 22.8186 40.5125 23.7018 39.4167ZM22 12.8334C23.2189 12.8334 24.3878 13.3163 25.2496 14.1758C26.1114 15.0354 26.5956 16.2012 26.5956 17.4167C26.5956 18.6323 26.1114 19.7981 25.2496 20.6577C24.3878 21.5172 23.2189 22.0001 22 22.0001C20.7812 22.0001 19.6123 21.5172 18.7505 20.6577C17.8887 19.7981 17.4045 18.6323 17.4045 17.4167C17.4045 16.2012 17.8887 15.0354 18.7505 14.1758C19.6123 13.3163 20.7812 12.8334 22 12.8334Z',
                  fillColor: isSelected ? '#A81421' : '#F27E88',
                  fillOpacity: 1,
                  strokeColor: '#A81421',
                  strokeOpacity: 1,
                  strokeWeight: 2,
                  anchor: { x: 22, y: 44 },
                }}
              />
            );
          })}
        </GoogleMap>
      </APIProvider>
    </Box>
  );
};

export default BranchesMap;
